import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const today = new Date().toISOString().split('T')[0];

const EMPTY_SUP = {
  supplierName: '', contact: '', email: '',
  address: '', city: '', gstNo: '', notes: ''
};

const EMPTY_PAY = { supplierId: '', paymentDate: today, amount: '', description: '' };

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AquaSuppliers({ role = 'admin' }) {
  const canWrite  = role !== 'readonly';
  const canDelete = role === 'admin';

  // Supplier master state
  const [suppliers, setSuppliers] = useState([]);
  const [supForm, setSupForm]     = useState(EMPTY_SUP);
  const [supEditId, setSupEditId] = useState(null);
  const [supErr, setSupErr]       = useState('');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);

  // Payment state
  const [payments, setPayments]     = useState([]);
  const [payForm, setPayForm]       = useState(EMPTY_PAY);
  const [payEditId, setPayEditId]   = useState(null);
  const [payErr, setPayErr]         = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [paySaving, setPaySaving]   = useState(false);
  const [filterSup, setFilterSup]   = useState('');

  // Ledger summary state
  const [ledger, setLedger]         = useState([]);
  const [purchases, setPurchases]   = useState([]);
  const [expanded, setExpanded]     = useState(new Set());

  const [tab, setTab] = useState('list');

  const loadSuppliers = () =>
    aquaApi.getSuppliers().then(d => setSuppliers(Array.isArray(d) ? d.slice().sort((a, b) => a.supplier_name.localeCompare(b.supplier_name)) : d)).finally(() => setLoading(false));

  const loadPayments = () => {
    setPayLoading(true);
    Promise.all([
      aquaApi.getSupplierPayments(filterSup || null),
      aquaApi.getSupplierLedger(),
      aquaApi.getStockPurchases(),
    ]).then(([p, l, sp]) => {
      setPayments(Array.isArray(p) ? p : []);
      setLedger(Array.isArray(l) ? l : []);
      setPurchases(Array.isArray(sp) ? sp : []);
    }).finally(() => setPayLoading(false));
  };

  useEffect(() => { loadSuppliers(); }, []);
  useEffect(() => { if (tab === 'payments') loadPayments(); }, [tab, filterSup]);

  const setS = (k, v) => setSupForm(f => ({ ...f, [k]: v }));
  const setP = (k, v) => setPayForm(f => ({ ...f, [k]: v }));

  // ── Supplier master handlers ──────────────────────────────────────────────────
  const handleSupSave = async () => {
    if (!supForm.supplierName.trim()) { setSupErr('Supplier name is required'); return; }
    try {
      if (supEditId) await aquaApi.updateSupplier(supEditId, supForm);
      else           await aquaApi.createSupplier(supForm);
      setSupForm(EMPTY_SUP); setSupEditId(null); setSupErr(''); setTab('list'); loadSuppliers();
    } catch (e) { setSupErr(e.message); }
  };

  const handleSupEdit = (r) => {
    setSupForm({
      supplierName: r.supplier_name || '', contact: r.contact || '',
      email: r.email || '', address: r.address || '',
      city: r.city || '', gstNo: r.gst_no || '', notes: r.notes || ''
    });
    setSupEditId(r.id); setTab('form');
  };

  // ── Payment handlers ──────────────────────────────────────────────────────────
  const handlePaySave = async () => {
    if (!payForm.supplierId) { setPayErr('Select a supplier'); return; }
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { setPayErr('Enter a valid amount'); return; }
    setPaySaving(true); setPayErr('');
    try {
      if (payEditId) await aquaApi.updateSupplierPayment(payEditId, payForm);
      else           await aquaApi.createSupplierPayment(payForm);
      setPayForm({ ...EMPTY_PAY }); setPayEditId(null); loadPayments();
    } catch (e) { setPayErr(e.message); }
    finally { setPaySaving(false); }
  };

  const handlePayEdit = (r) => {
    setPayForm({
      supplierId:  String(r.supplier_id),
      paymentDate: r.payment_date ? r.payment_date.split('T')[0] : today,
      amount:      String(r.amount || ''),
      description: r.description || '',
    });
    setPayEditId(r.id); setPayErr('');
  };

  const filtered = suppliers.filter(r =>
    !search ||
    (r.supplier_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.city || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.contact || '').toLowerCase().includes(search.toLowerCase())
  );

  const displayedPays = filterSup
    ? payments.filter(p => String(p.supplier_id) === String(filterSup))
    : payments;

  if (loading) return <div className="loading-msg">Loading suppliers…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`}
          onClick={() => { setTab('list'); setSupEditId(null); setSupForm(EMPTY_SUP); }}>
          📋 Supplier List
        </button>
        {canWrite && (
          <button className={`tab${tab === 'form' ? ' active' : ''}`}
            onClick={() => setTab('form')}>
            {supEditId ? '✏️ Edit Supplier' : '➕ Add Supplier'}
          </button>
        )}
        <button className={`tab${tab === 'payments' ? ' active' : ''}`}
          onClick={() => setTab('payments')}>
          💳 Payments
        </button>
      </div>

      {/* ── Supplier List ─────────────────────────────────────────────────────── */}
      {tab === 'list' && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-icon blue" style={{ fontSize: 22 }}>🚚</div>
              <div><div className="stat-label">Total Suppliers</div><div className="stat-value">{suppliers.length}</div></div>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">🏪 Suppliers</div>
              <input className="form-control" style={{ width: 240, padding: '4px 10px' }}
                placeholder="Search by name, city, contact…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Supplier Name</th><th>Contact</th><th>Email</th>
                    <th>City</th><th>GST No.</th><th>Address</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                      {search ? 'No matches found' : 'No suppliers added yet'}
                    </td></tr>
                  )}
                  {filtered.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{r.supplier_name}</td>
                      <td>{r.contact}</td>
                      <td>{r.email}</td>
                      <td>{r.city}</td>
                      <td><code style={{ fontSize: 12 }}>{r.gst_no}</code></td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.address}</td>
                      <td>
                        {canWrite  && <button className="btn-sm btn-primary"   onClick={() => handleSupEdit(r)} style={{ marginRight: 4 }}>✏️ Edit</button>}
                        {canDelete && <button className="btn-sm btn-danger"    onClick={async () => {
                          if (window.confirm(`Delete supplier "${r.supplier_name}"?`)) { await aquaApi.deleteSupplier(r.id); loadSuppliers(); }
                        }}>🗑 Del</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Supplier form ───────────────────────────────────────────── */}
      {tab === 'form' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">{supEditId ? '✏️ Edit Supplier' : '➕ Add Supplier'}</div>
          </div>
          <div className="card-body">
            {supErr && <div className="alert alert-error" style={{ marginBottom: 12 }}>{supErr}</div>}
            <div className="form-grid cols-3">
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label className="form-label">Supplier Name *</label>
                <input className="form-control" placeholder="e.g. SANKAR FEEDS PVT LTD"
                  value={supForm.supplierName} onChange={e => setS('supplierName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Contact / Phone</label>
                <input className="form-control" placeholder="Mobile number"
                  value={supForm.contact} onChange={e => setS('contact', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" placeholder="email@example.com"
                  value={supForm.email} onChange={e => setS('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-control" placeholder="City"
                  value={supForm.city} onChange={e => setS('city', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">GST No.</label>
                <input className="form-control" placeholder="29AAABBB1234Z1Z5"
                  value={supForm.gstNo} onChange={e => setS('gstNo', e.target.value.toUpperCase())} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Address</label>
                <input className="form-control" placeholder="Full address"
                  value={supForm.address} onChange={e => setS('address', e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <input className="form-control" placeholder="Any notes"
                  value={supForm.notes} onChange={e => setS('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSupSave}>{supEditId ? '💾 Update' : '💾 Save'}</button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setSupEditId(null); setSupForm(EMPTY_SUP); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Payments tab ──────────────────────────────────────────────────────── */}
      {tab === 'payments' && (
        <div>
          {/* Payment entry form */}
          {canWrite && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <div className="card-title">{payEditId ? '✏️ Edit Payment' : '💳 Record Payment'}</div>
              </div>
              <div className="card-body">
                {payErr && <div className="alert alert-error" style={{ marginBottom: 12 }}>{payErr}</div>}
                <div className="form-grid cols-2" style={{ gap: 14 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Supplier <span style={{ color: '#c5221f' }}>*</span></label>
                    <select className="form-control" value={payForm.supplierId}
                      onChange={e => setP('supplierId', e.target.value)}
                      style={!payForm.supplierId ? {} : {}}>
                      <option value="">— Select Supplier —</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Payment Date <span style={{ color: '#c5221f' }}>*</span></label>
                    <DateInput className="form-control" value={payForm.paymentDate}
                      onChange={e => setP('paymentDate', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Amount Paid (₹) <span style={{ color: '#c5221f' }}>*</span></label>
                    <input className="form-control" type="number" step="0.01" min="0"
                      placeholder="0.00" value={payForm.amount}
                      onChange={e => setP('amount', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Description / Note</label>
                    <input className="form-control" placeholder="e.g. Cheque #12345, NEFT"
                      value={payForm.description} onChange={e => setP('description', e.target.value)} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button className="btn btn-primary" onClick={handlePaySave} disabled={paySaving}>
                    {paySaving ? 'Saving…' : payEditId ? '💾 Update Payment' : '💾 Save Payment'}
                  </button>
                  {payEditId && (
                    <button className="btn btn-secondary" onClick={() => { setPayForm({ ...EMPTY_PAY }); setPayEditId(null); setPayErr(''); }}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Supplier ledger summary */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <div className="card-title">📊 Supplier Ledger Summary</div>
              <button onClick={loadPayments} style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid #e8eaed', background: '#fff', cursor: 'pointer', fontSize: 13 }}>↻ Refresh</button>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th style={{ textAlign: 'right' }}>Total Billed (₹)</th>
                    <th style={{ textAlign: 'right' }}>Paid in Invoice (₹)</th>
                    <th style={{ textAlign: 'right' }}>Additional Payments (₹)</th>
                    <th style={{ textAlign: 'right' }}>Interest (₹)</th>
                    <th style={{ textAlign: 'right' }}>Net Due (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No data</td></tr>
                  )}
                  {ledger.map(r => {
                    const due = parseFloat(r.net_due || 0);
                    const supInvoices = purchases.filter(p => p.vendor_name === r.supplier_name)
                      .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date));
                    const isExpanded = expanded.has(r.supplier_id);
                    return [
                      <tr key={r.supplier_id} style={{ cursor: supInvoices.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (supInvoices.length === 0) return;
                          setExpanded(prev => {
                            const next = new Set(prev);
                            if (next.has(r.supplier_id)) next.delete(r.supplier_id); else next.add(r.supplier_id);
                            return next;
                          });
                        }}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {supInvoices.length > 0 && (
                              <span style={{ fontSize: 10, color: '#888' }}>{isExpanded ? '▼' : '▶'}</span>
                            )}
                            <div>
                              <div style={{ fontWeight: 700 }}>{r.supplier_name}</div>
                              {r.contact && <div style={{ fontSize: 11, color: '#888' }}>{r.contact}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>₹{fmt(r.total_billed)}</td>
                        <td style={{ textAlign: 'right', color: '#137333' }}>₹{fmt(r.total_paid_purchase)}</td>
                        <td style={{ textAlign: 'right', color: '#137333' }}>₹{fmt(r.total_payments)}</td>
                        <td style={{ textAlign: 'right', color: '#c5221f' }}>₹{fmt(r.total_interest)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 15,
                          color: due > 0 ? '#c5221f' : due < 0 ? '#137333' : '#666' }}>
                          {due < 0 ? '−' : ''}₹{fmt(Math.abs(due))}
                        </td>
                      </tr>,
                      isExpanded && supInvoices.length > 0 && (
                        <tr key={`${r.supplier_id}-inv`}>
                          <td colSpan={6} style={{ padding: 0, background: '#f8faff' }}>
                            <div style={{ borderLeft: '3px solid #1a73e8', marginLeft: 32, marginRight: 12, marginTop: 4, marginBottom: 8 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ background: '#e8f0fe' }}>
                                    <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 700 }}>Date</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'left', fontWeight: 700 }}>Bill No</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>Amount (₹)</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>Paid (₹)</th>
                                    <th style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700 }}>Balance (₹)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {supInvoices.map(inv => {
                                    const bal = parseFloat(inv.purchase_amount || 0) - parseFloat(inv.payment_amount || 0);
                                    return (
                                      <tr key={inv.id} style={{ borderBottom: '1px solid #e8eaed' }}>
                                        <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>{fmtDate(inv.entry_date)}</td>
                                        <td style={{ padding: '4px 10px', fontWeight: 600, color: '#1a73e8' }}>{inv.bill_no || '—'}</td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right' }}>₹{fmt(inv.purchase_amount)}</td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', color: '#137333' }}>₹{fmt(inv.payment_amount)}</td>
                                        <td style={{ padding: '4px 10px', textAlign: 'right', fontWeight: 700, color: bal > 0 ? '#c5221f' : '#137333' }}>₹{fmt(bal)}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      ),
                    ].filter(Boolean);
                  })}
                </tbody>
                {ledger.length > 0 && (
                  <tfoot>
                    <tr>
                      <td style={{ fontWeight: 700, padding: '10px 12px' }}>TOTAL</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>₹{fmt(ledger.reduce((s, r) => s + parseFloat(r.total_billed || 0), 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>₹{fmt(ledger.reduce((s, r) => s + parseFloat(r.total_paid_purchase || 0), 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>₹{fmt(ledger.reduce((s, r) => s + parseFloat(r.total_payments || 0), 0))}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#c5221f' }}>₹{fmt(ledger.reduce((s, r) => s + parseFloat(r.total_interest || 0), 0))}</td>
                      {(() => { const t = ledger.reduce((s, r) => s + parseFloat(r.net_due || 0), 0); return (
                        <td style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px', color: t > 0 ? '#c5221f' : t < 0 ? '#137333' : '#666' }}>
                          {t < 0 ? '−' : ''}₹{fmt(Math.abs(t))}
                        </td>
                      ); })()}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Payment transactions list */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">💳 Payment Transactions</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Supplier:</label>
                <select className="form-control" style={{ width: 220, padding: '4px 8px' }}
                  value={filterSup} onChange={e => setFilterSup(e.target.value)}>
                  <option value="">All Suppliers</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_name}</option>)}
                </select>
              </div>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              {payLoading ? <div className="loading-msg">Loading…</div> : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Supplier</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th style={{ textAlign: 'right' }}>Amount Paid (₹)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPays.length === 0 && (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No payment transactions yet</td></tr>
                    )}
                    {displayedPays.map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight: 600 }}>{r.supplier_name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.payment_date)}</td>
                        <td style={{ color: '#5f6368' }}>{r.description || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#137333' }}>₹{fmt(r.amount)}</td>
                        <td>
                          {canWrite  && <button className="btn-sm btn-primary"  onClick={() => handlePayEdit(r)} style={{ marginRight: 4 }}>✏️ Edit</button>}
                          {canDelete && <button className="btn-sm btn-danger"   onClick={async () => {
                            if (window.confirm('Delete this payment?')) { await aquaApi.deleteSupplierPayment(r.id); loadPayments(); }
                          }}>🗑 Del</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {displayedPays.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>TOTAL PAID</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>
                          ₹{fmt(displayedPays.reduce((s, r) => s + parseFloat(r.amount || 0), 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
